import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AlertTriangle, MapPin } from "lucide-react"
import { MapRef } from "react-map-gl/mapbox"

import { MAP_PANEL, MAPBOX_TOKEN, MapShell, RouteLine } from "@/components/shared/MapShell"
import {
  boundsOf,
  edgePadding,
  type LngLat,
  overlappingStopIds,
  segmentLines,
} from "@/lib/mapGeometry"
import {
  SEGMENT_LINE_STYLE,
  type SegmentLineStyle,
  stopSegmentStates,
} from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { Route } from "@/types/collections"
import { getSlotTypeLabel, getSlotTypePinClasses } from "@/lib/slotTypeDisplay"

import DriverMarker, { DRIVER_STATUS_COLOURS } from "./map/DriverMarker"
import LocationPin, { type LocationEntry } from "./map/LocationPin"
import LocationPopup from "./map/LocationPopup"
import StopMarker from "./map/StopMarker"

interface RouteMapDisplayProps {
  routes: Route[]
  selectedRoute: Route | null
  onRouteSelect: (route: Route) => void
  onRouteClick?: (route: Route) => void
  /** Adds right padding to fitBounds so the route stays visible beside the side panel. */
  sidePanelOpen?: boolean
}

const routeBounds = (route: Route) => {
  const points = route.stops.map((s): LngLat => [s.lng, s.lat])
  if (route.currentLng && route.currentLat) points.push([route.currentLng, route.currentLat])
  return boundsOf(points)
}

const RouteMapDisplay: React.FC<RouteMapDisplayProps> = ({
  routes,
  selectedRoute,
  onRouteSelect,
  onRouteClick,
  sidePanelOpen = false,
}) => {
  const mapRef = useRef<MapRef>(null)
  const [viewState, setViewState] = useState({
    longitude: -0.1276,
    latitude: 51.5074,
    zoom: 11,
  })
  const [hoveredRoute, setHoveredRoute] = useState<Route | null>(null)
  /** Raises the hovered marker's z-index. */
  const [hoveredLocationKey, setHoveredLocationKey] = useState<string | null>(null)
  const [hoveredStopId, setHoveredStopId] = useState<string | null>(null)
  const [stopPopupInfo, setStopPopupInfo] = useState<{
    key: string
    stops: LocationEntry[]
    lng: number
    lat: number
  } | null>(null)
  const [hasInitialBounds, setHasInitialBounds] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)

  // A route with GPS coordinates means the vehicle is active.
  const activeRoutes = useMemo(() => {
    const active = routes.filter((r) => {
      if (!r.currentLat || !r.currentLng) return false
      // The vehicle may move after completion.
      if (r.status === "completed") return false
      return true
    })

    return active
  }, [routes])

  const allStopCoordinates = useMemo((): Array<[number, number]> => {
    const coords: Array<[number, number]> = []
    routes.forEach((route) => {
      route.stops.forEach((stop) => {
        if (stop.lat != null && stop.lng != null) {
          coords.push([stop.lng, stop.lat])
        }
      })
    })
    return coords
  }, [routes])

  const LOCATION_KEY_PRECISION = 5
  const pinsByLocation = useMemo(() => {
    if (selectedRoute) return []
    const flat = routes.flatMap((r) =>
      r.stops.filter((s) => s.lat != null && s.lng != null).map((stop) => ({ stop, route: r }))
    )
    const byKey = new globalThis.Map<string, LocationEntry[]>()
    flat.forEach(({ stop, route }) => {
      const key = `${Number(stop.lat).toFixed(LOCATION_KEY_PRECISION)}_${Number(stop.lng).toFixed(LOCATION_KEY_PRECISION)}`
      if (!byKey.has(key)) byKey.set(key, [])
      byKey.get(key)!.push({ stop, route })
    })
    return Array.from(byKey.entries()).map(([key, entries]) => ({
      key,
      lat: entries[0].stop.lat,
      lng: entries[0].stop.lng,
      entries,
    }))
  }, [routes, selectedRoute])

  const legendSlotTypes = useMemo(() => {
    const seen = new Set<string>()
    pinsByLocation.forEach(({ entries }) => {
      const stop = entries[0].stop
      const slotType = stop.slotType ?? (stop as { stop_type?: string }).stop_type
      if (slotType) seen.add(slotType)
    })
    return Array.from(seen).sort((a, b) => getSlotTypeLabel(a).localeCompare(getSlotTypeLabel(b)))
  }, [pinsByLocation])

  const overlappingStopsMap = useMemo(
    () => overlappingStopIds(selectedRoute?.stops ?? []),
    [selectedRoute]
  )

  const isStopOverlapping = useCallback(
    (stopId: string) => {
      return overlappingStopsMap.has(stopId)
    },
    [overlappingStopsMap]
  )

  const getOverlappingStops = useCallback(
    (stopId: string) => {
      const overlappingIds = overlappingStopsMap.get(stopId) || []
      if (!selectedRoute) return []
      return selectedRoute.stops.filter((s) => overlappingIds.includes(s.id))
    },
    [overlappingStopsMap, selectedRoute]
  )

  useEffect(() => {
    if (
      selectedRoute ||
      hasInitialBounds ||
      activeRoutes.length === 0 ||
      !mapLoaded ||
      !mapRef.current
    )
      return

    const bounds = boundsOf(
      activeRoutes
        .filter((r) => r.currentLat && r.currentLng)
        .map((r): LngLat => [r.currentLng!, r.currentLat!])
    )
    if (!bounds) return

    mapRef.current.fitBounds(bounds, { padding: edgePadding(50), duration: 500 })

    setHasInitialBounds(true)
  }, [activeRoutes, hasInitialBounds, mapLoaded, selectedRoute])

  // Extra right padding while the details panel (PANEL_WIDTH.default, 460px) is open.
  const fitBoundsPadding = useMemo(
    () => ({
      top: 50,
      bottom: 50,
      left: 50,
      right: sidePanelOpen ? 550 : 50,
    }),
    [sidePanelOpen]
  )

  useEffect(() => {
    if (!selectedRoute || !mapRef.current || !mapLoaded) return

    const bounds = routeBounds(selectedRoute)
    if (bounds) mapRef.current.fitBounds(bounds, { padding: fitBoundsPadding, duration: 500 })
  }, [selectedRoute, mapLoaded, fitBoundsPadding])

  useEffect(() => {
    if (
      selectedRoute ||
      hasInitialBounds ||
      allStopCoordinates.length === 0 ||
      !mapRef.current ||
      !mapLoaded
    )
      return

    mapRef.current.fitBounds(boundsOf(allStopCoordinates)!, {
      padding: edgePadding(80),
      duration: 500,
    })
    setHasInitialBounds(true)
  }, [selectedRoute, hasInitialBounds, allStopCoordinates, mapLoaded])

  // Same as the timeline: dashed grey = internal-to-internal, red = empty leg, grey = loaded.
  const mapSegmentStyles = useMemo(
    (): SegmentLineStyle[] =>
      stopSegmentStates(selectedRoute?.stops ?? []).map((state) => SEGMENT_LINE_STYLE[state]),
    [selectedRoute?.stops],
  )

  const routeLineData = useMemo(
    () =>
      selectedRoute?.stops?.length ? segmentLines(selectedRoute.stops, mapSegmentStyles) : null,
    [selectedRoute?.stops, mapSegmentStyles]
  )

  const handleRouteSelect = useCallback(
    (route: Route) => {
      onRouteSelect(route)

      const bounds = routeBounds(route)
      if (bounds && mapRef.current) {
        mapRef.current.fitBounds(bounds, { padding: fitBoundsPadding, duration: 500 })
      }
    },
    [onRouteSelect, fitBoundsPadding]
  )

  if (!MAPBOX_TOKEN) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-palette-jungleGreen100">
        <div className="max-w-md rounded-lg border border-palette-jungleGreen200 bg-white p-6 text-center shadow-lg">
          <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-amber-500" />
          <h3 className="mb-2 text-lg font-semibold text-palette-jungleGreen900">
            Mapbox Failed to Load
          </h3>
        </div>
      </div>
    )
  }

  const overlay = (
    <>
      <div
        className={cn("absolute bottom-4 left-4 z-10 p-3 shadow-md backdrop-blur-sm", MAP_PANEL)}
      >
        {!selectedRoute && (
          <>
            <div className="mb-2 text-xs font-medium text-palette-jungleGreen700">Stop type</div>
            <div className="mb-3 space-y-1.5">
              {legendSlotTypes.map((slotType) => (
                <div
                  key={slotType}
                  className="flex items-center gap-2 text-xs text-palette-jungleGreen600"
                >
                  <MapPin
                    size={14}
                    className={cn("flex-shrink-0", getSlotTypePinClasses(slotType))}
                  />
                  <span>{getSlotTypeLabel(slotType)}</span>
                </div>
              ))}
              <div className="mt-1 text-[10px] text-palette-jungleGreen500">
                Faded = served or skipped
              </div>
            </div>
          </>
        )}
        <div className="mb-2 text-xs font-medium text-palette-jungleGreen700">Driver status</div>
        <div className="space-y-1.5">
          {DRIVER_STATUS_COLOURS.map(({ key, label, colour }) => (
            <div key={key} className="flex items-center gap-2 text-xs text-palette-jungleGreen600">
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: colour }} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute left-4 top-4 z-10 flex flex-col gap-3">
        {!selectedRoute && (
          <div className={cn("max-w-xs p-3 shadow-md backdrop-blur-sm", MAP_PANEL)}>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 text-amber-500" />
              <div>
                <div className="text-sm font-medium text-palette-jungleGreen900">
                  All stops for the day
                </div>
                <div className="mt-0.5 text-xs text-palette-jungleGreen500">
                  Select a route from the list or click a driver marker to view full details
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <MapShell
      mapRef={mapRef}
      {...viewState}
      onMove={(evt) => setViewState(evt.viewState)}
      onLoad={() => setMapLoaded(true)}
      overlay={overlay}
    >
      {routeLineData && <RouteLine id="route-line" data={routeLineData} />}

      {!selectedRoute &&
        pinsByLocation.map(({ key, lat, lng, entries }) => (
          <LocationPin
            key={key}
            lat={lat}
            lng={lng}
            entries={entries}
            isHovered={hoveredLocationKey === key}
            isPopupOpen={stopPopupInfo?.key === key}
            onHoverChange={(hovered) => setHoveredLocationKey(hovered ? key : null)}
            onSelect={() => {
              setHoveredLocationKey(null)
              setStopPopupInfo({ key, stops: entries, lng, lat })
              mapRef.current?.flyTo({
                center: [lng, lat],
                zoom: Math.max(viewState.zoom, 13),
                duration: 500,
              })
            }}
          />
        ))}

      {/* Before driver markers so drivers render on top. */}
      {selectedRoute?.stops.map((stop, index) => (
        <StopMarker
          key={stop.id}
          stop={stop}
          index={index}
          routeStops={selectedRoute.stops}
          overlappingStops={getOverlappingStops(stop.id)}
          isOverlapping={isStopOverlapping(stop.id)}
          isHovered={hoveredStopId === stop.id}
          onHoverChange={setHoveredStopId}
        />
      ))}

      {stopPopupInfo && (
        <LocationPopup
          lat={stopPopupInfo.lat}
          lng={stopPopupInfo.lng}
          entries={stopPopupInfo.stops}
          onClose={() => setStopPopupInfo(null)}
          onRouteChoose={(route) => {
            handleRouteSelect(route)
            onRouteClick?.(route)
            setStopPopupInfo(null)
          }}
        />
      )}

      {activeRoutes.map((route) => {
        if (!route.currentLat || !route.currentLng) return null
        return (
          <DriverMarker
            key={route.id}
            route={route}
            lat={route.currentLat}
            lng={route.currentLng}
            isSelected={selectedRoute?.id === route.id}
            isHovered={hoveredRoute?.id === route.id}
            onHoverChange={setHoveredRoute}
            onSelect={(selected) => {
              handleRouteSelect(selected)
              onRouteClick?.(selected)
            }}
          />
        )
      })}
    </MapShell>
  )
}

export default RouteMapDisplay
